import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CampoDeMoeda, Aviso, Botao, Campo, Modal, useDialogo } from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import type { LinhaSped } from '../../servicos/operacao';
import { formatarCompetencia, moedaParaCampo } from '../../servicos/formato';
import { ErroDaApi } from '../ErroDaApi';

/**
 * RN-132, RN-073, RN-074 — Editar o cálculo de uma competência à mão.
 *
 * ## Quando isso acontece
 *
 * O SPED é a fonte, mas nem sempre a última palavra: o contribuinte retifica a
 * apuração, o mês vem com ajuste que o arquivo não reflete, ou o operador
 * fecha um percentual diferente para aquele mês. O Deca 1 permitia editar; aqui
 * a rota existia e não havia tela.
 *
 * ## Contrato fechado não se edita
 *
 * A API recusa (409) alterar cálculo de ano com contrato fechado — o deságio
 * daquele mês já foi materializado e o contribuinte já recebeu os números. A
 * tela pergunta o estado antes e diz isso de cara: oferecer "Editar" para
 * devolver um erro depois do formulário preenchido é pior que não oferecer.
 *
 * ## Os campos derivados continuam derivados
 *
 * Só o **ICMS apurado** e os **percentuais** são editáveis. Valor de
 * utilização, valor a pagar e benefício saem deles — deixar os sete campos
 * livres permitiria gravar uma linha em que a conta não fecha, e ninguém
 * conferiria de novo depois. Quem quiser um valor específico ajusta o
 * percentual que o produz.
 */
export function ModalDeEdicaoDoCalculo(props: {
  readonly clienteId: string;
  readonly linha: LinhaSped | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();

  // Mesma chave da tela de contrato: aproveita o cache em vez de repetir a
  // consulta.
  const anoDaLinha = props.linha === null ? null : Number(props.linha.competencia.slice(0, 4)) + 1;
  const contrato = useQuery({
    queryKey: ['estado-contrato', props.clienteId, anoDaLinha],
    queryFn: () =>
      requisicaoAutenticada<{ de: string }>(`/icms/contrato/${props.clienteId}/${anoDaLinha ?? 0}`),
    enabled: anoDaLinha !== null,
  });

  const [totalSped, setTotalSped] = useState('');
  const [percentSped, setPercentSped] = useState('');
  const [percentBeneficio, setPercentBeneficio] = useState('');

  // Os campos nascem do valor atual e só passam a valer quando tocados: enviar
  // o que não mudou reescreveria a linha com o mesmo número, e um `audUpdatedAt`
  // novo faria parecer que alguém alterou o mês.
  const valor = (digitado: string, atual: number | null): string =>
    digitado === '' ? (atual === null ? '' : String(atual)) : digitado;

  const salvar = useMutation({
    mutationFn: () => {
      const l = props.linha;
      if (l === null) throw new Error('Nenhuma competência selecionada.');

      const numero = (texto: string): number | undefined => {
        if (texto === '') return undefined;
        const n = Number(texto.replace(/\./g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : undefined;
      };

      return requisicaoAutenticada<unknown>(`/icms/speds/${props.clienteId}`, {
        metodo: 'PUT',
        corpo: {
          competencia: l.competencia,
          ...(numero(totalSped) === undefined ? {} : { totalSped: numero(totalSped) }),
          ...(numero(percentSped) === undefined
            ? {}
            : { percentSpedPercentual: numero(percentSped) }),
          ...(numero(percentBeneficio) === undefined
            ? {}
            : { percentBeneficioPercentual: numero(percentBeneficio) }),
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['speds', props.clienteId] });
      fechar();
    },
  });

  const excluir = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<unknown>(`/icms/speds/${props.linha?.id ?? ''}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['speds', props.clienteId] });
      fechar();
    },
  });

  const fechar = (): void => {
    salvar.reset();
    excluir.reset();
    setTotalSped('');
    setPercentSped('');
    setPercentBeneficio('');
    props.aoFechar();
  };

  const l = props.linha;
  const enviado = l?.statusEnvio === 'ENVIADO';
  const travado = contrato.data?.de === 'FECHADO';

  return (
    <Modal
      key={l?.id ?? 'nenhum'}
      aberto={l !== null}
      aoFechar={fechar}
      titulo="Editar cálculo do mês"
      descricao={l === null ? undefined : formatarCompetencia(l.competencia)}
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            disabled={travado}
            carregando={excluir.isPending}
            onClick={async () => {
              const ok = await confirmar({
                titulo: `Excluir o cálculo de ${l === null ? 'este mês' : formatarCompetencia(l.competencia)}?`,
                descricao: 'O mês some da operação. Reenviar o SPED o recria.',
                rotuloConfirmar: 'Excluir mês',
                tom: 'perigo',
              });
              if (ok) excluir.mutate();
            }}
          >
            Excluir mês
          </Botao>
          <Botao onClick={() => salvar.mutate()} disabled={travado} carregando={salvar.isPending}>
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {travado && (
          <Aviso tom="atencao" titulo={`O contrato de ${anoDaLinha ?? ''} está fechado`}>
            O deságio deste ano já foi materializado e o contribuinte já recebeu os números. Para
            corrigir um mês, reabra o contrato na aba de ICMS Recorrente — a reabertura é
            registrada.
          </Aviso>
        )}

        {enviado && !travado && (
          <Aviso tom="atencao" titulo="Este mês já foi aproveitado">
            O crédito já foi transferido e o certificado já veio. Alterar os números agora faz o
            relatório divergir do que o contribuinte recebeu.
          </Aviso>
        )}

        <CampoDeMoeda
          rotulo="ICMS apurado"
          ajuda="O total do mês, como veio no SPED. Alterar aqui recalcula o resto."
          value={totalSped === '' ? moedaParaCampo(l?.totalSped ?? null) : totalSped}
          onChange={setTotalSped}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="% de utilização"
            inputMode="decimal"
            ajuda="Quanto do apurado vira crédito utilizável."
            value={valor(percentSped, l?.percentSpedPercentual ?? null)}
            onChange={(e) => setPercentSped(e.target.value)}
          />
          <Campo
            rotulo="% de deságio"
            inputMode="decimal"
            ajuda="O desconto sobre o crédito — é daqui que sai o benefício."
            value={valor(percentBeneficio, l?.percentBeneficioPercentual ?? null)}
            onChange={(e) => setPercentBeneficio(e.target.value)}
          />
        </div>

        <Aviso tom="informacao">
          Valor de utilização, valor a pagar e benefício são <strong>calculados</strong> a partir
          destes três. Não são editáveis para não existir linha em que a conta não fecha.
        </Aviso>

        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
        {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}
      </div>
    </Modal>
  );
}

/**
 * RN-133 — Deságio de venda do ano inteiro.
 *
 * Aplica o mesmo percentual a todos os meses do exercício de uma vez. É a
 * operação que o escritório faz ao fechar a negociação: o número foi acordado
 * para o ano, não mês a mês.
 *
 * O corte por mês existe porque um cliente pode trocar de fornecedor no meio do
 * ano (RN-149): sem ele, aplicar o deságio novo reescreveria também os meses do
 * fornecedor anterior, que já foram fechados com outro número.
 */
export function ModalDeDesagioDoAno(props: {
  readonly clienteId: string;
  readonly ano: number;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [percentual, setPercentual] = useState('');
  const [mesInicio, setMesInicio] = useState('');

  const aplicar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<unknown>(`/icms/speds/${props.clienteId}/${props.ano}`, {
        metodo: 'POST',
        corpo: {
          desagioPercentual: Number(percentual.replace(',', '.')),
          ...(mesInicio === '' ? {} : { mesInicio: Number(mesInicio) }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['speds', props.clienteId] });
      fechar();
    },
  });

  const fechar = (): void => {
    aplicar.reset();
    setPercentual('');
    setMesInicio('');
    props.aoFechar();
  };

  const numero = Number(percentual.replace(',', '.'));
  const valido = percentual !== '' && Number.isFinite(numero) && numero >= 0 && numero <= 100;

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Deságio de venda do ano"
      descricao={`Exercício ${props.ano}`}
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => aplicar.mutate()} disabled={!valido} carregando={aplicar.isPending}>
            Aplicar a todos os meses
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          rotulo="Deságio (%)"
          inputMode="decimal"
          ajuda="O que o cliente paga sobre o crédito. De 0 a 100."
          value={percentual}
          onChange={(e) => setPercentual(e.target.value)}
        />
        <Campo
          rotulo="A partir do mês (opcional)"
          type="number"
          min={1}
          max={12}
          ajuda="Deixe vazio para o ano inteiro. Use quando o cliente trocou de fornecedor no meio do ano e os meses anteriores já estão fechados."
          value={mesInicio}
          onChange={(e) => setMesInicio(e.target.value)}
        />

        <Aviso tom="atencao">
          Isto reescreve o deságio de <strong>todos</strong> os meses do período — inclusive os que
          já foram enviados ao contribuinte.
        </Aviso>

        {aplicar.isError && <ErroDaApi titulo="Não foi possível aplicar" erro={aplicar.error} />}
      </div>
    </Modal>
  );
}
